// src/features/whatsapp/components/WhatsAppDisclaimerModal.tsx
// ============================================================================
// MODAL DE DESCARGO DE RESPONSABILIDAD — ANTES DE VINCULAR WHATSAPP
// ============================================================================
//
// Se muestra cuando el negocio intenta vincular WhatsApp y aún NO ha aceptado
// el aviso de riesgo de baneo del número. Bloquea el flujo de QR hasta que
// marque el checkbox y confirme. La aceptación se persiste en Firestore
// (ver whatsappDisclaimerService.ts) y queda registrada con fecha.
// ============================================================================

import React, { useState } from 'react';
import { ShieldAlert, Loader2, X } from 'lucide-react';

interface Props {
  /** Se invoca al confirmar (ya con checkbox marcado). Debe persistir la aceptación. */
  onAccept: () => Promise<void> | void;
  /** Cierra el modal sin aceptar. */
  onCancel: () => void;
  /** True mientras se guarda la aceptación. */
  accepting?: boolean;
}

const WhatsAppDisclaimerModal: React.FC<Props> = ({ onAccept, onCancel, accepting }) => {
  const [checked, setChecked] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 modal-overlay">
      <div className="bg-surface rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto modal-panel">

        {/* Header */}
        <div className="px-5 py-4 sm:px-6 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-500/10 dark:to-orange-500/10 flex items-start justify-between rounded-t-lg">
          <h3 className="text-base font-semibold text-content flex items-center pr-4">
            <ShieldAlert className="h-5 w-5 mr-2 text-amber-600 dark:text-amber-400 flex-shrink-0" />
            Antes de vincular tu WhatsApp
          </h3>
          <button
            onClick={onCancel}
            disabled={accepting}
            className="text-content-3 hover:text-content-2 disabled:opacity-50 flex-shrink-0"
            aria-label="Cerrar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Cuerpo */}
        <div className="px-5 py-4 sm:px-6 space-y-4 text-sm text-content-2">
          <p>
            Lealify envía los mensajes desde <strong className="text-content">tu propio número</strong> a
            través de una integración no oficial de WhatsApp. WhatsApp (Meta){' '}
            <strong className="text-content">no autoriza</strong> el envío automatizado por esta vía, por lo
            que existe el <strong className="text-content">riesgo de que tu número sea limitado, suspendido
            o bloqueado</strong>, sobre todo si envías a muchos contactos en poco tiempo o a personas que no
            te conocen.
          </p>

          <div className="bg-surface-2 rounded-lg p-3">
            <p className="font-medium text-content mb-1.5">Para reducir el riesgo te recomendamos:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Usar un <strong className="text-content">número dedicado al negocio</strong>, no tu número personal principal.</li>
              <li><strong className="text-content">No enviar campañas masivas de golpe</strong>; respeta los límites y los tiempos.</li>
              <li>Escribir solo a clientes que dieron su número y <strong className="text-content">respetar a quien pida no recibir mensajes</strong>.</li>
            </ul>
          </div>

          <p>
            <strong className="text-content">Lealify no se hace responsable</strong> de la suspensión o
            bloqueo del número de WhatsApp que decidas vincular. Al continuar, confirmas que entiendes y
            aceptas este riesgo.
          </p>

          <label className="flex items-start gap-2.5 p-3 rounded-md border border-line cursor-pointer hover:bg-surface-2">
            <input
              type="checkbox"
              checked={checked}
              onChange={e => setChecked(e.target.checked)}
              disabled={accepting}
              className="h-4 w-4 mt-0.5 text-green-600 dark:text-green-400 border-line rounded focus:ring-green-500"
            />
            <span className="text-content">He leído y acepto este descargo de responsabilidad.</span>
          </label>
        </div>

        {/* Acciones */}
        <div className="px-5 py-4 sm:px-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 border-t border-line">
          <button
            onClick={onCancel}
            disabled={accepting}
            className="inline-flex justify-center items-center px-4 py-2 border border-line text-sm font-medium rounded-md text-content-2 bg-surface hover:bg-surface-2 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={() => onAccept()}
            disabled={!checked || accepting}
            className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {accepting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Acepto y continúo
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppDisclaimerModal;
